import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { isIndividualClub } from "../../constants/individualClub";
import MobileAppLayout from "../../layouts/MobileAppLayout";
import api from "../../services/api";
import { GameCard, GameHero, GameIcon, SegmentedControl, Skeleton } from "../../components/game";

type LeaderboardEntry = {
  rank: number;
  user_id: string;
  full_name: string;
  avatar_url: string | null;
  points: number;
  team_name?: string | null;
  club_name?: string | null;
  isCurrentUser?: boolean;
};

type LeaderboardPeriod = "daily" | "weekly" | "monthly" | "yearly" | "alltime";
type LeaderboardScope = "global" | "club" | "team";

export default function AppLeaderboard() {
  const { t } = useTranslation();
  const { user, activeClubId } = useAuth();
  // Individual (club-less) accounts have no meaningful club/team ranking — the
  // "club" is the shared placeholder pool — so they only get the global board.
  const individual = isIndividualClub(activeClubId);

  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [showingFallback, setShowingFallback] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [period, setPeriod] = useState<LeaderboardPeriod>("weekly");
  const [scope, setScope] = useState<LeaderboardScope>(individual ? "global" : "club");
  const [currentUserRank, setCurrentUserRank] = useState<LeaderboardEntry | null>(null);
  const [myTeams, setMyTeams] = useState<{ id: string; name: string }[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>("");

  // Load the athlete's teams once, so the team-scope board can target a specific
  // team when they belong to several.
  useEffect(() => {
    if (individual) return;
    api.get("/profiles/me/teams")
      .then(({ data }) => {
        const ts = data.teams || [];
        setMyTeams(ts);
        if (ts.length) setSelectedTeam((prev) => prev || ts[0].id);
      })
      .catch(() => {});
  }, [individual]);

  useEffect(() => {
    const loadLeaderboard = async () => {
      setLoading(true);
      setError(false);
      try {
        const params = new URLSearchParams({
          period,
          scope,
          limit: "50",
        });
        if (activeClubId && scope === "club") {
          params.append("clubId", activeClubId);
        }
        if (scope === "team" && selectedTeam) {
          params.append("teamId", selectedTeam);
        }

        const { data } = await api.get(`/leaderboard?${params.toString()}`);
        const entriesWithUser = data.entries.map((entry: LeaderboardEntry) => ({
          ...entry,
          isCurrentUser: entry.user_id === user?.id,
        }));
        setEntries(entriesWithUser);
        setShowingFallback(!!data.showing_fallback);

        // Find current user's rank
        const userEntry = entriesWithUser.find((e: LeaderboardEntry) => e.isCurrentUser);
        setCurrentUserRank(userEntry || null);
      } catch (err) {
        console.error("Failed to load leaderboard:", err);
        setError(true);
      }
      setLoading(false);
    };
    loadLeaderboard();
  }, [period, scope, activeClubId, user?.id, selectedTeam]);

  // Sub-line under each athlete's name — different content per scope.
  // Club scope: team only. Global scope: "club · team" on one row.
  // Team + alltime scope: nothing (everyone's in the same team).
  const subLineFor = (entry: LeaderboardEntry): string | null => {
    if (scope === "club") return entry.team_name || null;
    if (scope === "global") {
      const parts = [entry.club_name, entry.team_name].filter(Boolean);
      return parts.length > 0 ? parts.join(" · ") : null;
    }
    return null;
  };

  // Copy for the fallback banner — when today has no entries and we
  // slid back to yesterday, or this week to last week.
  const fallbackMessage = (): string | null => {
    if (!showingFallback) return null;
    if (period === "daily") return "Nu s-au acumulat puncte astăzi. Iată clasamentul de ieri:";
    if (period === "weekly") return "Săptămâna abia a început. Iată clasamentul săptămânii trecute:";
    return null;
  };

  const getMedalColor = (rank: number) => {
    switch (rank) {
      case 1:
        return "from-amber-300 via-yellow-400 to-amber-500";
      case 2:
        return "from-slate-300 via-slate-200 to-slate-400";
      case 3:
        return "from-orange-400 via-amber-500 to-orange-600";
      default:
        return "";
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Five segments on a phone: the labels have to be short or the row clips.
  const periodLabels: Record<LeaderboardPeriod, string> = {
    daily: t("leaderboard.daily", "Azi"),
    weekly: t("leaderboard.weekly", "Săpt."),
    monthly: t("leaderboard.monthly", "Lună"),
    yearly: t("leaderboard.yearly", "An"),
    alltime: t("leaderboard.alltime", "Total"),
  };

  const scopeLabels: Record<LeaderboardScope, string> = {
    global: t("leaderboard.global", "Global"),
    club: t("leaderboard.club", "Club"),
    team: t("leaderboard.team", "Echipă"),
  };

  const visibleScopes = individual
    ? (["global"] as LeaderboardScope[])
    : (Object.keys(scopeLabels) as LeaderboardScope[]);
  const scopeOptions = visibleScopes.map((s) => ({
    value: s,
    label: scopeLabels[s],
  }));
  const periodOptions = (Object.keys(periodLabels) as LeaderboardPeriod[]).map((p) => ({
    value: p,
    label: periodLabels[p],
  }));

  // Small amber "points" pill — star, never a coin (kids app).
  const PointsBadge = ({ value, className = "" }: { value: number; className?: string }) => (
    <span className={`inline-flex items-center gap-1 font-extrabold tabular-nums ${className}`}>
      <GameIcon name="star" className="w-4 h-4" />
      {value}
    </span>
  );

  return (
    <MobileAppLayout>
      <div className="px-4 py-6 space-y-5">
        {/* Header — unified gradient hero (matches Sessions) */}
        <GameHero flow className="-mx-4 -mt-6 rounded-br-3xl px-4 pt-3 pb-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-extrabold">{t("leaderboard.title", "Clasament")}</h1>
              <p className="text-white/70 text-[13px] mt-0.5">{t("leaderboard.subtitle", "Vezi cum te compari cu ceilalți")}</p>
            </div>
            <Link
              to="/app/points-info"
              className="flex items-center gap-1.5 px-3 py-2 bg-white/15 hover:bg-white/25 text-white rounded-xl font-bold text-[13px] transition-colors shrink-0"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t("leaderboard.pointsInfo", "Info puncte")}
            </Link>
          </div>
        </GameHero>

        {/* Current User Rank Card */}
        {loading ? (
          <Skeleton className="h-24 rounded-2xl" />
        ) : currentUserRank && (
          <GameHero className="rounded-2xl p-5 shadow-[0_4px_0_#3730a3]">
            <div className="flex items-center gap-4">
              <div className="relative">
                {user?.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt={user.full_name || undefined}
                    className="w-16 h-16 rounded-2xl object-cover border-2 border-white/30"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center text-xl font-extrabold">
                    {getInitials(user?.full_name || "U")}
                  </div>
                )}
                <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center text-indigo-600 font-extrabold text-sm shadow-[0_2px_0_#c7d2fe]">
                  #{currentUserRank.rank}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white/80 text-sm font-semibold">{t("leaderboard.yourRank", "Locul tău")}</p>
                <h2 className="text-2xl font-extrabold">#{currentUserRank.rank}</h2>
                <div className="mt-1.5 inline-flex items-center gap-1.5 bg-white/15 rounded-2xl px-2.5 py-1">
                  <PointsBadge value={currentUserRank.points} className="text-white" />
                  <span className="text-white/80 text-sm font-semibold">{t("leaderboard.points", "puncte")}</span>
                </div>
              </div>
            </div>
          </GameHero>
        )}

        {/* Scope Filter */}
        <SegmentedControl
          className="w-full flex"
          options={scopeOptions}
          value={scope}
          onChange={setScope}
        />

        {/* Team picker — only in team scope when the athlete is on several teams */}
        {scope === "team" && myTeams.length > 1 && (
          <select
            value={selectedTeam}
            onChange={(e) => setSelectedTeam(e.target.value)}
            className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {myTeams.map((tm) => (
              <option key={tm.id} value={tm.id}>{tm.name}</option>
            ))}
          </select>
        )}

        {/* Period Filter */}
        <SegmentedControl
          className="w-full flex"
          options={periodOptions}
          value={period}
          onChange={setPeriod}
        />

        {/* Fallback banner — daily/weekly with no data in the current
            bucket, showing yesterday / last week instead. Header sits
            above the podium so the athlete knows what window they're
            reading before scanning ranks. */}
        {!loading && showingFallback && fallbackMessage() && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 text-sm font-semibold text-amber-800 shadow-[0_3px_0_#fde68a]">
            {fallbackMessage()}
          </div>
        )}

        {/* Leaderboard List */}
        {loading ? (
          <GameCard className="overflow-hidden divide-y divide-slate-100">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-4">
                {/* Rank */}
                <Skeleton className="w-8 h-8 rounded-xl" />
                {/* Avatar */}
                <Skeleton className="w-10 h-10 rounded-2xl" />
                {/* Name + sub-line */}
                <div className="flex-1 min-w-0 space-y-1.5">
                  <Skeleton className="h-3.5 w-2/5 rounded-full" />
                  <Skeleton className="h-2.5 w-1/4 rounded-full" />
                </div>
                {/* Points */}
                <Skeleton className="h-4 w-10 rounded-full" />
              </div>
            ))}
          </GameCard>
        ) : entries.length > 0 ? (
          <div className="space-y-3">
            {/* Top 3 Podium — only when ≥3 entries. With <3 we skip
                the podium and render the small leaderboard as a flat
                list below. Previously the page rendered nothing for
                1-2 entries (no podium, slice(3) = empty list). */}
            {entries.length >= 3 && (
              <div className="flex items-end justify-center gap-2 py-4 mb-4">
                {/* 2nd Place */}
                <div className="flex flex-col items-center">
                  <div className="relative">
                    {entries[1].avatar_url ? (
                      <img
                        src={entries[1].avatar_url}
                        alt={entries[1].full_name}
                        className={`w-14 h-14 rounded-2xl object-cover border-2 ${
                          entries[1].isCurrentUser ? "border-indigo-500" : "border-slate-300"
                        }`}
                      />
                    ) : (
                      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${getMedalColor(2)} flex items-center justify-center text-white font-extrabold text-lg`}>
                        {getInitials(entries[1].full_name)}
                      </div>
                    )}
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
                      <GameIcon name="medal" className="w-7 h-7 drop-shadow" />
                    </div>
                  </div>
                  <div className="bg-white border border-slate-100 rounded-2xl w-20 h-16 mt-3 flex flex-col items-center justify-center shadow-[0_3px_0_#eef2f8]">
                    <p className="text-xs font-bold text-slate-600 truncate max-w-full px-1">{entries[1].full_name.split(" ")[0]}</p>
                    <PointsBadge value={entries[1].points} className="text-sm text-slate-800 mt-0.5" />
                  </div>
                </div>

                {/* 1st Place */}
                <div className="flex flex-col items-center -mt-4">
                  <div className="relative">
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2">
                      <GameIcon name="trophy" className="w-8 h-8 drop-shadow" />
                    </div>
                    {entries[0].avatar_url ? (
                      <img
                        src={entries[0].avatar_url}
                        alt={entries[0].full_name}
                        className={`rounded-2xl object-cover border-4 ${
                          entries[0].isCurrentUser ? "border-indigo-500" : "border-amber-400"
                        }`}
                        style={{ width: "4.5rem", height: "4.5rem" }}
                      />
                    ) : (
                      <div className={`rounded-2xl bg-gradient-to-br ${getMedalColor(1)} flex items-center justify-center text-white font-extrabold text-xl`} style={{ width: "4.5rem", height: "4.5rem" }}>
                        {getInitials(entries[0].full_name)}
                      </div>
                    )}
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-7 h-7 bg-amber-400 rounded-full flex items-center justify-center text-white font-extrabold text-sm shadow-[0_2px_0_#b45309]">
                      1
                    </div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-2xl w-24 h-20 mt-3 flex flex-col items-center justify-center shadow-[0_3px_0_#fde68a]">
                    <p className="text-xs font-bold text-amber-700 truncate max-w-full px-1">{entries[0].full_name.split(" ")[0]}</p>
                    <PointsBadge value={entries[0].points} className="text-lg text-amber-800 mt-0.5" />
                  </div>
                </div>

                {/* 3rd Place */}
                <div className="flex flex-col items-center">
                  <div className="relative">
                    {entries[2].avatar_url ? (
                      <img
                        src={entries[2].avatar_url}
                        alt={entries[2].full_name}
                        className={`w-12 h-12 rounded-2xl object-cover border-2 ${
                          entries[2].isCurrentUser ? "border-indigo-500" : "border-orange-400"
                        }`}
                      />
                    ) : (
                      <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${getMedalColor(3)} flex items-center justify-center text-white font-extrabold`}>
                        {getInitials(entries[2].full_name)}
                      </div>
                    )}
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center text-white font-extrabold text-xs shadow-[0_2px_0_#c2410c]">
                      3
                    </div>
                  </div>
                  <div className="bg-white border border-slate-100 rounded-2xl w-16 h-14 mt-3 flex flex-col items-center justify-center shadow-[0_3px_0_#eef2f8]" style={{ width: "4.5rem" }}>
                    <p className="text-xs font-bold text-orange-700 truncate max-w-full px-1">{entries[2].full_name.split(" ")[0]}</p>
                    <PointsBadge value={entries[2].points} className="text-sm text-slate-800 mt-0.5" />
                  </div>
                </div>
              </div>
            )}

            {/* List below the podium. When ≥3 entries are present
                we skip the top 3 (they're on the podium); otherwise
                we render the whole list flat. */}
            <GameCard className="overflow-hidden divide-y divide-slate-100">
              {entries.slice(entries.length >= 3 ? 3 : 0).map((entry) => (
                <Link
                  key={entry.user_id}
                  to={`/app/profile/${entry.user_id}`}
                  className={`flex items-center gap-3 p-4 hover:bg-slate-50 active:bg-slate-100 transition-colors ${
                    entry.isCurrentUser ? "bg-indigo-50" : ""
                  }`}
                >
                  {/* Rank */}
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-extrabold text-sm ${
                    entry.isCurrentUser
                      ? "bg-indigo-600 text-white"
                      : "bg-slate-100 text-slate-600"
                  }`}>
                    {entry.rank}
                  </div>

                  {/* Avatar */}
                  {entry.avatar_url ? (
                    <img
                      src={entry.avatar_url}
                      alt={entry.full_name}
                      className="w-10 h-10 rounded-2xl object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-slate-600 font-bold text-sm">
                      {getInitials(entry.full_name)}
                    </div>
                  )}

                  {/* Name + optional sub-line (team / club·team). */}
                  <div className="flex-1 min-w-0">
                    <p className={`font-bold truncate ${
                      entry.isCurrentUser ? "text-indigo-700" : "text-slate-800"
                    }`}>
                      {entry.full_name}
                      {entry.isCurrentUser && (
                        <span className="ml-2 text-xs bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full font-bold">
                          {t("leaderboard.you", "Tu")}
                        </span>
                      )}
                    </p>
                    {subLineFor(entry) && (
                      <p className="text-xs text-slate-500 truncate mt-0.5">
                        {subLineFor(entry)}
                      </p>
                    )}
                  </div>

                  {/* Points */}
                  <PointsBadge value={entry.points} className="text-amber-600" />
                </Link>
              ))}
            </GameCard>
          </div>
        ) : error ? (
          <GameCard className="p-8 text-center">
            <h3 className="text-lg font-extrabold text-slate-800 mb-2">
              {t("common.loadErrorTitle", "Ceva n-a mers")}
            </h3>
            <p className="text-slate-500 text-sm mb-3">
              {t("common.loadError", "Nu am putut încărca datele. Verifică conexiunea și încearcă din nou.")}
            </p>
            <button onClick={() => window.location.reload()} className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white">
              {t("common.retry", "Reîncearcă")}
            </button>
          </GameCard>
        ) : (
          <GameCard className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-indigo-50 rounded-2xl flex items-center justify-center">
              <GameIcon name="trophy" className="w-9 h-9" />
            </div>
            <h3 className="text-lg font-extrabold text-slate-800 mb-2">
              {t("leaderboard.noEntries", "Niciun clasament disponibil")}
            </h3>
            <p className="text-slate-500 text-sm">
              {t("leaderboard.noEntriesDesc", "Completează quiz-uri și participă la antrenamente pentru a acumula puncte")}
            </p>
          </GameCard>
        )}
      </div>
    </MobileAppLayout>
  );
}
